import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";
import { ChevronRight, Search, Mail, Phone, Car, Shield, Lock, MapPin, CheckCircle2, XCircle } from "lucide-react";

/**
 * Public marketing page explaining the private multi-shop quote model.
 * SEO-critical: Google needs the H1, intro, and FAQ text without JS.
 * Content is 100% static so it renders instantly on first paint —
 * search-engine crawlers running JS-less get the full text.
 */
export default function HowItWorksPage() {
  const navigate = useNavigate();

  const faq = [
    {
      q: "Do shops see each other's quotes?",
      a: "No. Each shop submits their quote privately. Shops can't see rivals' prices, contact details, or notes — they compete blind.",
    },
    {
      q: "Will shops get my phone number straight away?",
      a: "Your contact details stay hidden until you choose a shop and proceed. You keep control of who you talk to.",
    },
    {
      q: "How many quotes will I get?",
      a: "It depends on live local shop coverage and your search radius. Where multiple shops are active nearby you can receive several quotes; if coverage is thin, try widening the radius.",
    },
    {
      q: "Is TyreMatch the seller?",
      a: "No. TyreMatch is a marketplace. You pay the shop directly for the tyres and fitment — TyreMatch is not the tyre seller or the payment processor for fitment.",
    },
    {
      q: "Can I delete a request?",
      a: "Yes. You can delete a request at any time from your dashboard. Shops that already quoted won't be able to contact you afterwards.",
    },
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://tyrematch.co.nz/" },
      { "@type": "ListItem", position: 2, name: "How it works", item: "https://tyrematch.co.nz/how-it-works" },
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO page="howItWorks" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-heading font-bold text-lg text-primary" data-testid="hiw-home-link">
            TyreMatch
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/tread-depth-nz" className="text-muted-foreground hover:text-primary">Tread depth</Link>
            <Button size="sm" onClick={() => navigate("/customer/auth")} data-testid="hiw-cta-quote-btn">Get quotes</Button>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-10">
        <div>
          <p className="text-sm text-primary font-semibold uppercase tracking-wider mb-2">How it works</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold leading-tight mb-4">
            How private multi-shop tyre quotes work
          </h1>
          <p className="text-lg text-muted-foreground">
            Ringing around five tyre shops for prices is a hassle. TyreMatch lets you request once — nearby shops send private quotes so you can compare and book. Your details stay hidden until you choose.
          </p>
        </div>

        {/* 3 steps */}
        <section className="grid sm:grid-cols-3 gap-4">
          {[
            { icon: Search, title: "1. Request", body: "Enter your rego (or tyre size), the service(s) you need, and your search radius." },
            { icon: Mail, title: "2. Receive private quotes", body: "Nearby local shops send quotes. Shops can't see each other's prices." },
            { icon: CheckCircle2, title: "3. Compare & book", body: "Compare ratings and price, pick a shop, and book fitment. Payment happens at the shop." },
          ].map((s, i) => (
            <div key={i} className="rounded-xl border p-4 bg-card">
              <s.icon className="h-6 w-6 text-primary mb-2" />
              <h3 className="font-semibold mb-1">{s.title}</h3>
              <p className="text-sm text-muted-foreground">{s.body}</p>
            </div>
          ))}
        </section>

        {/* What "private" means */}
        <section>
          <h2 className="font-heading text-2xl font-bold mb-3 flex items-center gap-2"><Lock className="h-5 w-5 text-primary" /> What "private" means</h2>
          <ul className="space-y-2 text-muted-foreground">
            <li>• Shops compete blind — they don't see rival quotes or your notes to other shops.</li>
            <li>• Your contact details stay hidden until you choose a shop and proceed.</li>
            <li>• You set the search radius, so you decide how far you're willing to travel.</li>
          </ul>
        </section>

        {/* Services */}
        <section>
          <h2 className="font-heading text-2xl font-bold mb-3">Services you can request</h2>
          <p className="text-muted-foreground mb-2">Combine any of these in a single request:</p>
          <ul className="grid sm:grid-cols-3 gap-2 text-sm">
            <li className="rounded-lg border p-3">Tyres (supplied &amp; fitted)</li>
            <li className="rounded-lg border p-3">Wheel alignment</li>
            <li className="rounded-lg border p-3">Tyre rotation</li>
          </ul>
        </section>

        {/* Comparison table */}
        <section>
          <h2 className="font-heading text-2xl font-bold mb-3">Old way vs TyreMatch</h2>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-3 py-2">Task</th>
                  <th className="text-left px-3 py-2 text-muted-foreground">Ringing around</th>
                  <th className="text-left px-3 py-2 text-primary">TyreMatch</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {[
                  ["Contacting shops", "One at a time, on the phone", "One request, sent to nearby shops"],
                  ["Comparing prices", "Notes, re-quoting, memory", "Side-by-side, in one view"],
                  ["Privacy", "Shops know who else you called", "Shops can't see rivals' quotes"],
                  ["Booking", "Repeat your details every time", "Book with your chosen shop"],
                ].map((row, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 font-medium">{row[0]}</td>
                    <td className="px-3 py-2 text-muted-foreground">{row[1]}</td>
                    <td className="px-3 py-2">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Local shops note */}
        <section className="rounded-xl border-l-4 border-primary bg-primary/5 p-4">
          <p className="text-sm">
            <strong>Local NZ shops only.</strong> TyreMatch is a marketplace of New Zealand tyre shops — not an overseas warehouse shipping service. You pay the shop directly at fitment.
          </p>
        </section>

        {/* Tips */}
        <section>
          <h2 className="font-heading text-2xl font-bold mb-3">Tips for better quotes</h2>
          <ul className="space-y-2 text-muted-foreground">
            <li>• Enter your rego — it saves the shop time (and gets you a more accurate quote).</li>
            <li>• Use the note field to mention a brand preference or deadline.</li>
            <li>• Widen your search radius if few shops respond.</li>
            <li>• Compare on more than price — check ratings and stock availability too.</li>
          </ul>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="font-heading text-2xl font-bold mb-3">FAQ</h2>
          <div className="space-y-3">
            {faq.map((f, i) => (
              <details key={i} className="rounded-lg border px-3 py-2 bg-card">
                <summary className="cursor-pointer font-medium py-1">{f.q}</summary>
                <p className="text-sm text-muted-foreground mt-2">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="rounded-2xl bg-primary text-primary-foreground p-6 text-center">
          <h2 className="font-heading text-2xl font-bold mb-2">Ready to get private quotes?</h2>
          <p className="text-primary-foreground/85 mb-4 text-sm">One request. Nearby local shops. Compare privately.</p>
          <Button
            size="lg"
            onClick={() => navigate("/customer/auth")}
            className="bg-background text-foreground hover:bg-background/90 rounded-full"
            data-testid="hiw-closing-cta"
          >
            Get Your Free Quotes Now
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </section>
      </main>

      <footer className="border-t py-6 text-center text-xs text-muted-foreground">
        <div className="max-w-3xl mx-auto px-4">
          TyreMatch is a marketplace connecting NZ drivers with local tyre shops. You pay the shop directly.
        </div>
      </footer>
    </div>
  );
}
